import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const fallback =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function SoldProperties() {
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const isMobile = screenWidth <= 700;
  const isTablet = screenWidth <= 1050;

  useEffect(() => {
    carregarVendidos();

    const resize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  const carregarVendidos = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        "/properties/sold/full",
        {
          withCredentials: true
        }
      );

      setProperties(res.data || []);
    } catch (err) {
      console.log(err);

      if (err?.response?.status === 401) {
        navigate("/");
      }

      setProperties([]);
    }

    setLoading(false);
  };

  const restaurarImovel = async (propertyId) => {
    try {
      const confirmar = window.confirm(
        "Deseja restaurar este imóvel para a lista de imóveis ativos?"
      );

      if (!confirmar) return;

      setRestoringId(propertyId);

      await axios.put(
        `/properties/${propertyId}/restore`,
        {},
        {
          withCredentials: true
        }
      );

      await carregarVendidos();
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao restaurar imóvel";

      alert(msg);
    }

    setRestoringId(null);
  };

  const excluirDefinitivo = async (propertyId) => {
    try {
      const confirmar = window.confirm(
        "Tem certeza que deseja excluir este imóvel definitivamente?"
      );

      if (!confirmar) return;

      const confirmarFinal = window.confirm(
        "Essa ação não pode ser desfeita. Deseja continuar?"
      );

      if (!confirmarFinal) return;

      setDeletingId(propertyId);

      await axios.delete(
        `/properties/${propertyId}/permanent`,
        {
          withCredentials: true
        }
      );

      await carregarVendidos();
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao excluir imóvel definitivamente";

      alert(msg);
    }

    setDeletingId(null);
  };

  const abrirGoogleMaps = (property) => {
    const temCoordenadas =
      property?.latitude !== null &&
      property?.latitude !== undefined &&
      property?.longitude !== null &&
      property?.longitude !== undefined &&
      property?.latitude !== "" &&
      property?.longitude !== "";

    const enderecoTexto = [
      property?.endereco,
      property?.bairro,
      property?.cidade
    ]
      .filter(Boolean)
      .join(", ");

    const query = temCoordenadas
      ? `${property.latitude},${property.longitude}`
      : enderecoTexto;

    if (!query) {
      alert("Este imóvel não possui localização cadastrada");
      return;
    }

    const url =
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(query);

    window.open(url, "_blank");
  };

  const money = (value) => {
    return Number(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  const formatDate = (value) => {
    if (!value) return "Data não informada";

    return new Date(value).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const montarPagamentosComImovel = (property) => {
    const payments = property.payments || [];

    const existeImovel = payments.some((item) => {
      return item.titulo === "Imóvel";
    });

    if (existeImovel) {
      return payments;
    }

    return [
      {
        id: "imovel-automatico",
        property_id: property.id,
        titulo: "Imóvel",
        descricao: "Valor pago pelo imóvel",
        valor: Number(property.valor_pago || 0),
        responsavel_user_id: null,
        automatico: true
      },
      ...payments
    ];
  };

  const calcularResumo = (property) => {
    const payments = montarPagamentosComImovel(property);
    const shares = property.profitShares || [];

    const gastosSemRestituicao = payments.filter((item) => {
      return !item.responsavel_user_id;
    });

    const gastosComRestituicao = payments.filter((item) => {
      return item.responsavel_user_id;
    });

    const totalGastosSemRestituicao = gastosSemRestituicao.reduce(
      (total, item) => {
        return total + Number(item.valor || 0);
      },
      0
    );

    const totalGastosComRestituicao = gastosComRestituicao.reduce(
      (total, item) => {
        return total + Number(item.valor || 0);
      },
      0
    );

    const totalGastosCadastrados =
      totalGastosSemRestituicao + totalGastosComRestituicao;

    const qtdParticipantes = shares.length || 1;

    const gastoSemRestituicaoPorPessoa =
      totalGastosSemRestituicao / qtdParticipantes;

    const gastoComRestituicaoPorPessoa =
      totalGastosComRestituicao / qtdParticipantes;

    const totalPagoComRestituicaoUsuario = (userId) => {
      return gastosComRestituicao
        .filter((item) => {
          return String(item.responsavel_user_id || "") === String(userId);
        })
        .reduce((total, item) => {
          return total + Number(item.valor || 0);
        }, 0);
    };

    const usuarios = shares.map((item) => {
      const percentual = Number(item.percentual || 0);

      const parteVenda =
        Number(property.valor_venda || 0) *
        (percentual / 100);

      const totalPagoRestituicao =
        totalPagoComRestituicaoUsuario(item.user_id);

      const saldoRestituicao =
        totalPagoRestituicao - gastoComRestituicaoPorPessoa;

      const valorFinal =
        parteVenda -
        gastoSemRestituicaoPorPessoa +
        saldoRestituicao;

      return {
        ...item,
        percentual,
        parteVenda,
        totalPagoRestituicao,
        saldoRestituicao,
        valorFinal
      };
    });

    const totalADistribuir =
      Number(property.valor_venda || 0) -
      totalGastosSemRestituicao;

    return {
      payments,
      gastosSemRestituicao,
      gastosComRestituicao,
      totalGastosSemRestituicao,
      totalGastosComRestituicao,
      totalGastosCadastrados,
      qtdParticipantes,
      gastoSemRestituicaoPorPessoa,
      gastoComRestituicaoPorPessoa,
      totalADistribuir,
      usuarios
    };
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.loading}>
          Carregando imóveis vendidos...
        </p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div
        style={{
          ...styles.container,
          padding: isMobile ? 14 : 24
        }}
      >
        <div style={styles.header}>
          <p style={styles.tag}>
            Histórico de vendas
          </p>

          <h1
            style={{
              ...styles.title,
              fontSize: isMobile ? 26 : 38
            }}
          >
            Imóveis Vendidos
          </h1>

          <p style={styles.subtitle}>
            Veja os imóveis vendidos, gastos, restituições e o valor final a receber por usuário.
          </p>
        </div>

        {properties.length === 0 ? (
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}>
              🏠
            </div>

            <h2 style={styles.emptyTitle}>
              Nenhum imóvel vendido
            </h2>

            <p style={styles.emptyText}>
              Quando um imóvel for marcado como vendido, ele aparecerá aqui.
            </p>
          </div>
        ) : (
          <div style={styles.list}>
            {properties.map((property) => {
              const resumo = calcularResumo(property);

              return (
                <div key={property.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitleBox}>
                      <div style={styles.soldBadge}>
                        Vendido
                      </div>

                      <h2 style={styles.cardTitle}>
                        {property.nome}
                      </h2>

                      <p style={styles.cardSubtitle}>
                        Vendido em {formatDate(property.vendido_em)}
                      </p>

                      <div style={styles.locationRow}>
                        <button
                          onClick={() => abrirGoogleMaps(property)}
                          style={styles.mapButton}
                          title="Abrir no Google Maps"
                        >
                          📍
                        </button>

                        <span>
                          {property.bairro || "Bairro não informado"} •{" "}
                          {property.cidade || "Cidade não informada"}
                        </span>
                      </div>
                    </div>

                    {property.canEdit === true && (
                      <div style={styles.actions}>
                        <button
                          onClick={() => restaurarImovel(property.id)}
                          disabled={restoringId === property.id}
                          style={styles.restoreButton}
                        >
                          {restoringId === property.id
                            ? "Restaurando..."
                            : "Restaurar"}
                        </button>

                        <button
                          onClick={() => excluirDefinitivo(property.id)}
                          disabled={deletingId === property.id}
                          style={styles.deleteButton}
                        >
                          {deletingId === property.id
                            ? "Excluindo..."
                            : "Excluir"}
                        </button>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      ...styles.summaryGrid,
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(3, 1fr)"
                    }}
                  >
                    <div style={styles.summaryBox}>
                      <span>Valor por venda</span>
                      <strong>{money(property.valor_venda)}</strong>
                    </div>

                    <div style={styles.summaryBox}>
                      <span>Soma dos gastos total</span>
                      <strong>{money(resumo.totalGastosCadastrados)}</strong>
                    </div>

                    <div style={styles.summaryBoxBlue}>
                      <span>Total a distribuir</span>
                      <strong>{money(resumo.totalADistribuir)}</strong>
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.contentGrid,
                      gridTemplateColumns: isTablet ? "1fr" : "1.1fr .9fr"
                    }}
                  >
                    <div style={styles.sectionCard}>
                      <div style={styles.sectionHeader}>
                        <div>
                          <h3 style={styles.sectionTitle}>
                            Valor final por usuário
                          </h3>

                          <p style={styles.sectionSubtitle}>
                            O cálculo usa somente o saldo líquido da restituição.
                          </p>
                        </div>
                      </div>

                      <div style={styles.userList}>
                        {resumo.usuarios.map((user) => (
                          <div key={user.user_id} style={styles.userItem}>
                            <div style={styles.userTop}>
                              <div style={styles.userInfo}>
                                <img
                                  src={user.foto || fallback}
                                  alt={user.nome}
                                  style={styles.avatar}
                                />

                                <div style={styles.userNameBox}>
                                  <strong>{user.nome}</strong>
                                  <span>{user.email}</span>
                                </div>
                              </div>

                              <div style={styles.percentBadge}>
                                {Number(user.percentual || 0).toFixed(2)}%
                              </div>
                            </div>

                            <div style={styles.calcBox}>
                              <div style={styles.calcRow}>
                                <span>Parte da venda</span>
                                <strong>{money(user.parteVenda)}</strong>
                              </div>

                              {resumo.gastoSemRestituicaoPorPessoa > 0 && (
                                <div style={styles.calcRow}>
                                  <span>Gasto sem restituição</span>
                                  <strong>
                                    -{money(resumo.gastoSemRestituicaoPorPessoa)}
                                  </strong>
                                </div>
                              )}

                              {resumo.totalGastosComRestituicao > 0 && (
                                <>
                                  <div style={styles.calcRowMuted}>
                                    <span>Parte nos gastos com restituição</span>
                                    <strong>
                                      {money(resumo.gastoComRestituicaoPorPessoa)}
                                    </strong>
                                  </div>

                                  <div style={styles.calcRowMuted}>
                                    <span>Valor que ele pagou</span>
                                    <strong>
                                      {money(user.totalPagoRestituicao)}
                                    </strong>
                                  </div>

                                  <div
                                    style={{
                                      ...styles.calcRow,
                                      color:
                                        user.saldoRestituicao >= 0
                                          ? "#86efac"
                                          : "#fca5a5"
                                    }}
                                  >
                                    <span>Saldo líquido da restituição</span>
                                    <strong>
                                      {user.saldoRestituicao >= 0 ? "+" : "-"}
                                      {money(Math.abs(user.saldoRestituicao))}
                                    </strong>
                                  </div>
                                </>
                              )}

                              <div style={styles.finalRow}>
                                <span>Final a receber</span>
                                <strong
                                  style={{
                                    color:
                                      user.valorFinal >= 0
                                        ? "#86efac"
                                        : "#fca5a5"
                                  }}
                                >
                                  {money(user.valorFinal)}
                                </strong>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={styles.sectionCard}>
                      <div style={styles.sectionHeader}>
                        <div>
                          <h3 style={styles.sectionTitle}>
                            Gastos do imóvel
                          </h3>

                          <p style={styles.sectionSubtitle}>
                            Comum ou com restituição.
                          </p>
                        </div>
                      </div>

                      <div style={styles.expenseSummary}>
                        <div>
                          <span>Sem restituição</span>
                          <strong>{money(resumo.totalGastosSemRestituicao)}</strong>
                        </div>

                        <div>
                          <span>Com restituição</span>
                          <strong>{money(resumo.totalGastosComRestituicao)}</strong>
                        </div>
                      </div>

                      <div style={styles.expenseList}>
                        {resumo.payments.map((payment, index) => {
                          const isImovel = payment.titulo === "Imóvel";

                          return (
                            <div
                              key={payment.id || index}
                              style={{
                                ...styles.expenseItem,
                                border: isImovel
                                  ? "1px solid rgba(96,165,250,0.32)"
                                  : "1px solid rgba(255,255,255,0.06)",
                                background: isImovel
                                  ? "rgba(37,99,235,0.12)"
                                  : "#111827"
                              }}
                            >
                              <div style={styles.expenseNumber}>
                                {index + 1}
                              </div>

                              <div style={styles.expenseContent}>
                                <div style={styles.expenseTop}>
                                  <strong>{payment.titulo}</strong>
                                  <strong>{money(payment.valor)}</strong>
                                </div>

                                <p style={styles.expenseType}>
                                  {payment.responsavel_user_id
                                    ? `Pago por ${
                                        payment.responsavel_nome || "Usuário"
                                      } • com restituição`
                                    : "Sem restituição • descontado igualmente"}
                                </p>

                                {payment.descricao && (
                                  <p style={styles.expenseDescription}>
                                    {payment.descricao}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#0f172a",
    color: "#fff",
    boxSizing: "border-box"
  },

  container: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    boxSizing: "border-box"
  },

  loading: {
    padding: 30,
    color: "#cbd5e1"
  },

  header: {
    marginBottom: 24
  },

  tag: {
    color: "#60a5fa",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    marginBottom: 8
  },

  title: {
    margin: 0,
    color: "#f8fafc",
    fontWeight: 800,
    lineHeight: 1.1
  },

  subtitle: {
    marginTop: 10,
    color: "#94a3b8",
    fontSize: 15,
    lineHeight: 1.5
  },

  emptyCard: {
    minHeight: 360,
    borderRadius: 24,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(15,23,42,0.95)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 24
  },

  emptyIcon: {
    fontSize: 58,
    marginBottom: 16
  },

  emptyTitle: {
    margin: 0,
    color: "#f8fafc"
  },

  emptyText: {
    color: "#94a3b8",
    maxWidth: 420,
    lineHeight: 1.5
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 22
  },

  card: {
    background: "rgba(15,23,42,0.96)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: 22,
    boxSizing: "border-box",
    boxShadow: "0 24px 70px rgba(0,0,0,0.28)"
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap",
    marginBottom: 18
  },

  cardTitleBox: {
    minWidth: 0
  },

  soldBadge: {
    width: "fit-content",
    padding: "7px 11px",
    borderRadius: 999,
    background: "rgba(34,197,94,0.13)",
    color: "#86efac",
    border: "1px solid rgba(34,197,94,0.32)",
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10
  },

  cardTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 25,
    lineHeight: 1.2
  },

  cardSubtitle: {
    margin: "7px 0 0",
    color: "#94a3b8",
    fontSize: 13
  },

  locationRow: {
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#cbd5e1",
    fontSize: 13
  },

  mapButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: "1px solid rgba(96,165,250,0.25)",
    background: "rgba(37,99,235,0.14)",
    color: "#bfdbfe",
    cursor: "pointer"
  },

  actions: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    flexWrap: "wrap"
  },

  restoreButton: {
    padding: "11px 14px",
    borderRadius: 12,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontWeight: "bold"
  },

  deleteButton: {
    padding: "11px 14px",
    borderRadius: 12,
    border: "1px solid rgba(239,68,68,0.35)",
    background: "rgba(239,68,68,0.13)",
    color: "#fca5a5",
    cursor: "pointer",
    fontWeight: "bold"
  },

  summaryGrid: {
    display: "grid",
    gap: 12,
    marginBottom: 18
  },

  summaryBox: {
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 18,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8
  },

  summaryBoxBlue: {
    background: "rgba(37,99,235,0.14)",
    border: "1px solid rgba(96,165,250,0.28)",
    borderRadius: 18,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8
  },

  contentGrid: {
    display: "grid",
    gap: 18,
    alignItems: "start"
  },

  sectionCard: {
    background: "#020617",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 20,
    padding: 18,
    boxSizing: "border-box"
  },

  sectionHeader: {
    marginBottom: 14
  },

  sectionTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 18
  },

  sectionSubtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.4
  },

  userList: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },

  userItem: {
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 16,
    padding: 14
  },

  userTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 12
  },

  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0
  },

  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0
  },

  userNameBox: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0
  },

  percentBadge: {
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(34,197,94,0.12)",
    color: "#86efac",
    fontSize: 12,
    fontWeight: "bold",
    whiteSpace: "nowrap"
  },

  calcBox: {
    display: "flex",
    flexDirection: "column",
    gap: 7
  },

  calcRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#cbd5e1",
    fontSize: 13
  },

  calcRowMuted: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#64748b",
    fontSize: 12
  },

  finalRow: {
    marginTop: 8,
    paddingTop: 10,
    borderTop: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#f8fafc",
    fontSize: 15,
    fontWeight: "bold"
  },

  expenseSummary: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 12
  },

  expenseList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 520,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 6,
    scrollbarWidth: "thin"
  },

  expenseItem: {
    display: "flex",
    gap: 10,
    borderRadius: 14,
    padding: 12
  },

  expenseNumber: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "rgba(37,99,235,0.16)",
    color: "#93c5fd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: "bold",
    flexShrink: 0
  },

  expenseContent: {
    flex: 1,
    minWidth: 0
  },

  expenseTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: "#f8fafc",
    fontSize: 14,
    flexWrap: "wrap"
  },

  expenseType: {
    margin: "5px 0 0",
    color: "#93c5fd",
    fontSize: 12
  },

  expenseDescription: {
    margin: "5px 0 0",
    color: "#94a3b8",
    fontSize: 12,
    lineHeight: 1.4
  }
};